import { FunctionComponent } from 'react'
import styles from './desktopNav.module.scss'
import MenuIcon from '@mui/icons-material/Menu'
import Link from '../../../_custom/Link/Link'
import { Role } from '@/types/Role'
import { Daycare, DaycareType } from '@/types/Daycare'
import { Location, useLocation } from 'react-router-dom'
import { CalendarMonth, Home, ListAlt, MenuOpen, Restaurant, Sms } from '@mui/icons-material'
import FamiliesIcon from '@mui/icons-material/SupervisedUserCircle'
import { daycareTypeToString } from '@/modules/helper/daycareHelper'
import { useTranslation } from 'react-i18next'
import ButtonLink from '../ButtonLink/buttonLink'
import GroupIcon from '@mui/icons-material/HolidayVillage'
import Assignment from '@mui/icons-material/Assignment'

interface DesktopNavProps {
    userRole: Role
    logoBase64Data?: string
    newsCount: number
    canReadContracts: boolean
    canReadFamilies: boolean
    canReadNews: boolean
    hasNewsAccess: boolean
    hasSuggestAccess: boolean
    canReadSuggestions: boolean
    hasTaskNoteAccess: boolean
    canReadTaskNotes: boolean
    hasMealAccess: boolean
    canReadMeal: boolean
    daycare?: Daycare
    daycaresLength: number
}

const DesktopNav: FunctionComponent<DesktopNavProps> = ({
    userRole,
    logoBase64Data,
    newsCount,
    canReadFamilies,
    canReadContracts,
    canReadNews,
    hasNewsAccess,
    hasSuggestAccess,
    hasTaskNoteAccess,
    canReadSuggestions,
    canReadTaskNotes,
    hasMealAccess,
    canReadMeal,
    daycare,
    daycaresLength
}: DesktopNavProps) => {
    const { t } = useTranslation()

    const location: Location = useLocation()

    const daycareTypeName: string =
        daycaresLength === 1 && daycare ? daycareTypeToString(daycare.type, t, true) : t('structures')

    const homeIconActive: boolean =
        location.pathname === '/home' ||
        location.pathname.startsWith('/account') ||
        location.pathname === '/settings' ||
        (location.pathname === '/daycare' && userRole === Role.staff)

    const isRpeOrLaep: boolean = daycare?.type === DaycareType.rpe || daycare?.type === DaycareType.laep

    return (
        <div className={styles.DesktopNav}>
            <div className={styles.NavLogo}>
                <Link href={'/calendar'}>
                    <img src='/images/cald.svg' className={styles.NavLogoCALD} alt='Logo CALD' />
                    {userRole === Role.parent ? (
                        <img src='/images/logo_parent_iso.png' className={`${styles.NavLogoUser}`} alt='Logo parent' />
                    ) : (
                        <img
                            src='/images/logo_daycare_iso.png'
                            className={`${styles.NavLogoUser}`}
                            alt='Logo structure'
                        />
                    )}
                    {logoBase64Data ? (
                        <img
                            src={`data:image/png;base64,${logoBase64Data}`}
                            alt={`${daycare?.name} logo`}
                            className={styles.NavLogoDaycare}
                        />
                    ) : (
                        <></>
                    )}
                </Link>
            </div>
            <div className={styles.NavMenu}>
                <div className={styles.ButtonsLinks}>
                    {userRole !== Role.staff && (
                        <ButtonLink
                            href={'/daycare'}
                            locationActive={location.pathname === '/daycare'}
                            role={userRole}
                            icon={<Home className={styles.Icon} />}
                            text={daycareTypeName}
                        />
                    )}
                    {(canReadContracts || canReadFamilies) && (
                        <ButtonLink
                            href={isRpeOrLaep ? '/adults' : canReadContracts ? '/childs' : '/adults'}
                            locationActive={location.pathname === '/childs' || location.pathname === '/adults'}
                            role={userRole}
                            icon={<FamiliesIcon className={styles.Icon} />}
                            text={isRpeOrLaep ? t('publics') : t('families')}
                        />
                    )}
                    {(userRole === Role.cald || userRole === Role.trustee) && (
                        <ButtonLink
                            href={'/group'}
                            locationActive={location.pathname === '/group'}
                            role={userRole}
                            icon={<GroupIcon className={styles.Icon} />}
                            text={t('group')}
                        />
                    )}
                    {hasTaskNoteAccess && canReadTaskNotes && (
                        <ButtonLink
                            href={'/taskNote'}
                            locationActive={location.pathname === '/taskNote'}
                            role={userRole}
                            icon={<ListAlt className={styles.Icon} />}
                            text={t('tasksAndNotes')}
                        />
                    )}
                    {hasMealAccess && canReadMeal && (
                        <ButtonLink
                            href={'/meal'}
                            locationActive={location.pathname === '/meal'}
                            role={userRole}
                            icon={<Restaurant className={styles.Icon} />}
                            text={t('meal')}
                        />
                    )}
                    {hasSuggestAccess && canReadSuggestions && (
                        <ButtonLink
                            href={'/suggest'}
                            locationActive={location.pathname === '/suggest'}
                            role={userRole}
                            icon={<Assignment className={styles.Icon} />}
                            text={t('suggestions')}
                        />
                    )}
                    {daycare && (
                        <ButtonLink
                            href={'/calendar'}
                            locationActive={location.pathname === '/calendar'}
                            role={userRole}
                            icon={<CalendarMonth className={styles.Icon} />}
                            text={t('planning')}
                        />
                    )}
                    {hasNewsAccess && canReadNews && (
                        <ButtonLink
                            href={'/news'}
                            locationActive={location.pathname.startsWith('/news') || location.pathname === '/events'}
                            role={userRole}
                            icon={<Sms className={styles.Icon} />}
                            text={t('news')}
                            badge={userRole === Role.parent ? newsCount : 0}
                        />
                    )}
                    <ButtonLink
                        href={'/home'}
                        locationActive={homeIconActive}
                        role={userRole}
                        icon={
                            homeIconActive ? <MenuOpen className={styles.Icon} /> : <MenuIcon className={styles.Icon} />
                        }
                        text={t('menu')}
                    />
                </div>
            </div>
        </div>
    )
}

export default DesktopNav
